import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Package,
  Search,
  Eye,
  Edit2,
  Trash2,
  Plus,
  Loader2,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Users,
  Building2,
  GraduationCap,
  Star,
  Check,
  ToggleLeft,
  ToggleRight,
  Layers,
  Tag,
} from 'lucide-react'
import {
  adminApi,
  AdminProduct,
  ProductStatus,
  PricingType,
  BillingPeriod,
  CreateProductRequest,
  UpdateProductRequest,
  CreateProductPricingRequest,
  ProductPricing,
  TARGET_AUDIENCE_OPTIONS,
} from '@/api/admin'

// Constants
const PRODUCT_STATUSES: ProductStatus[] = ['draft', 'active', 'archived', 'discontinued']
const PRICING_TYPES: PricingType[] = ['one_time', 'subscription']
const BILLING_PERIODS: BillingPeriod[] = ['monthly', 'quarterly', 'yearly', 'lifetime']

// Labels
const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  draft: '초안',
  active: '활성',
  archived: '보관',
  discontinued: '종료',
}

const PRICING_TYPE_LABELS: Record<PricingType, string> = {
  one_time: '일회성',
  subscription: '구독',
}

const BILLING_PERIOD_LABELS: Record<BillingPeriod, string> = {
  monthly: '월간',
  quarterly: '분기',
  yearly: '연간',
  lifetime: '평생',
}

// Colors
const PRODUCT_STATUS_COLORS: Record<ProductStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  active: 'bg-green-100 text-green-700',
  archived: 'bg-yellow-100 text-yellow-700',
  discontinued: 'bg-red-100 text-red-700',
}

// Target Audience Icons
const TARGET_AUDIENCE_ICONS: Record<string, React.ElementType> = {
  individual: Users,
  parent: Users,
  student: GraduationCap,
  business: Building2,
  academy: GraduationCap,
  school: GraduationCap,
  all: Users,
}

// ============================================================================
// Product Form Modal
// ============================================================================
interface ProductFormModalProps {
  product: AdminProduct | null
  onClose: () => void
  onSave: (data: CreateProductRequest | UpdateProductRequest) => void
  isLoading: boolean
}

function ProductFormModal({ product, onClose, onSave, isLoading }: ProductFormModalProps) {
  const [formData, setFormData] = useState<CreateProductRequest>({
    name: product?.name ?? '',
    slug: product?.slug ?? '',
    shortDescription: product?.shortDescription ?? '',
    description: product?.description ?? '',
    thumbnailUrl: product?.thumbnailUrl ?? '',
    bannerUrl: product?.bannerUrl ?? '',
    previewVideoUrl: product?.previewVideoUrl ?? '',
    features: product?.features ?? [],
    targetAudience: product?.targetAudience ?? 'individual',
    learningOutcomes: product?.learningOutcomes ?? '',
    status: product?.status ?? 'draft',
    isActive: product?.isActive ?? true,
    isFeatured: product?.isFeatured ?? false,
    hasStockLimit: product?.hasStockLimit ?? false,
    stockQuantity: product?.stockQuantity ?? -1,
    displayOrder: product?.displayOrder ?? 0,
    tags: product?.tags ?? '',
    category: product?.category ?? '',
    curriculumContentId: product?.curriculumContentId ?? undefined,
  })

  const [featuresText, setFeaturesText] = useState(formData.features?.join('\n') ?? '')

  // Curriculum 목록 조회
  const { data: curriculums = [] } = useQuery({
    queryKey: ['admin', 'contents', 'curriculum', 'all'],
    queryFn: () => adminApi.getContentsByLevel('curriculum', true), // includeInactive=true
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const features = featuresText.split('\n').filter(f => f.trim())
    onSave({ ...formData, features })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-slate-200 z-10">
          <h2 className="text-xl font-bold text-slate-900">
            {product ? '상품 수정' : '새 상품 만들기'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 기본 정보 */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              기본 정보
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">상품명 *</label>
                <input
                  type="text"
                  className="input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">슬러그 (URL)</label>
                <input
                  type="text"
                  className="input"
                  value={formData.slug ?? ''}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="예: python-basic-course"
                />
              </div>
            </div>

            <div>
              <label className="label">짧은 설명</label>
              <input
                type="text"
                className="input"
                value={formData.shortDescription ?? ''}
                onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                maxLength={500}
              />
            </div>

            <div>
              <label className="label">상세 설명</label>
              <textarea
                className="input min-h-[120px] resize-y"
                value={formData.description ?? ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>

          {/* 대상 및 분류 */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-600" />
              대상 및 분류
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">대상 고객 *</label>
                <select
                  className="input"
                  value={formData.targetAudience ?? 'individual'}
                  onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                >
                  {TARGET_AUDIENCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">카테고리</label>
                <input
                  type="text"
                  className="input"
                  value={formData.category ?? ''}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="예: 프로그래밍, 데이터분석"
                />
              </div>
              <div>
                <label className="label">태그 (콤마로 구분)</label>
                <input
                  type="text"
                  className="input"
                  value={formData.tags ?? ''}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="예: 초급, 파이썬, 입문"
                />
              </div>
            </div>

            {/* Curriculum 연결 */}
            <div>
              <label className="label">연결된 커리큘럼</label>
              <select
                className="input"
                value={formData.curriculumContentId ?? ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  curriculumContentId: e.target.value ? Number(e.target.value) : undefined 
                })}
              >
                <option value="">선택 안 함</option>
                {curriculums.map((curriculum) => (
                  <option key={curriculum.id} value={curriculum.id}>
                    {curriculum.title}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                상품과 연결할 커리큘럼을 선택하세요. 구매 시 해당 커리큘럼에 접근할 수 있습니다.
              </p>
            </div>
          </div>

          {/* 미디어 */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" />
              미디어
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">썸네일 URL</label>
                <input
                  type="url"
                  className="input"
                  value={formData.thumbnailUrl ?? ''}
                  onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                />
              </div>
              <div>
                <label className="label">배너 URL</label>
                <input
                  type="url"
                  className="input"
                  value={formData.bannerUrl ?? ''}
                  onChange={(e) => setFormData({ ...formData, bannerUrl: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="label">미리보기 영상 URL</label>
              <input
                type="url"
                className="input"
                value={formData.previewVideoUrl ?? ''}
                onChange={(e) => setFormData({ ...formData, previewVideoUrl: e.target.value })}
              />
            </div>
          </div>

          {/* 마케팅 정보 */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              마케팅 정보
            </h3>
            
            <div>
              <label className="label">주요 특징 (한 줄에 하나씩)</label>
              <textarea
                className="input min-h-[100px] resize-y"
                value={featuresText}
                onChange={(e) => setFeaturesText(e.target.value)}
                placeholder="실무에 바로 적용 가능한 커리큘럼&#10;전문 강사의 1:1 피드백&#10;수료증 발급"
              />
            </div>

            <div>
              <label className="label">학습 성과</label>
              <textarea
                className="input min-h-[100px] resize-y"
                value={formData.learningOutcomes ?? ''}
                onChange={(e) => setFormData({ ...formData, learningOutcomes: e.target.value })}
                placeholder="이 상품을 통해 얻을 수 있는 학습 성과를 입력하세요"
              />
            </div>
          </div>

          {/* 상태 및 옵션 */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Tag className="w-5 h-5 text-green-600" />
              상태 및 옵션
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">상태 *</label>
                <select
                  className="input"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ProductStatus })}
                >
                  {PRODUCT_STATUSES.map((status) => (
                    <option key={status} value={status}>{PRODUCT_STATUS_LABELS[status]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">표시 순서</label>
                <input
                  type="number"
                  className="input"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
              <div>
                <label className="label">재고 수량 (-1: 무제한)</label>
                <input
                  type="number"
                  className="input"
                  value={formData.stockQuantity}
                  onChange={(e) => setFormData({ ...formData, stockQuantity: parseInt(e.target.value) || -1 })}
                  min={-1}
                  disabled={!formData.hasStockLimit}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded-sm border-slate-300 text-amber-600 focus:ring-amber-500"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span className="text-sm font-medium text-slate-700">활성화</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded-sm border-slate-300 text-amber-600 focus:ring-amber-500"
                  checked={formData.isFeatured}
                  onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                />
                <span className="text-sm font-medium text-slate-700">추천 상품</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded-sm border-slate-300 text-amber-600 focus:ring-amber-500"
                  checked={formData.hasStockLimit}
                  onChange={(e) => setFormData({ ...formData, hasStockLimit: e.target.checked })}
                />
                <span className="text-sm font-medium text-slate-700">재고 제한</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary btn-md"
              disabled={isLoading}
            >
              취소
            </button>
            <button
              type="submit"
              className="btn-primary btn-md"
              disabled={isLoading || !formData.name}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  저장 중...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {product ? '저장' : '생성'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ============================================================================
// Pricing Form Modal
// ============================================================================
interface PricingFormModalProps {
  pricing: ProductPricing | null
  productId: number
  productName: string
  onClose: () => void
  onSave: (data: CreateProductPricingRequest) => void
  isLoading: boolean
}

function PricingFormModal({ pricing, productId, productName, onClose, onSave, isLoading }: PricingFormModalProps) {
  const [formData, setFormData] = useState<CreateProductPricingRequest>({
    productId,
    name: pricing?.name ?? '',
    pricingType: pricing?.pricingType ?? 'one_time',
    billingPeriod: pricing?.billingPeriod ?? 'monthly',
    billingPeriodMonths: pricing?.billingPeriodMonths ?? 1,
    price: pricing?.price ?? 0,
    currency: pricing?.currency ?? 'KRW',
    originalPrice: pricing?.originalPrice ?? undefined,
    discountPercentage: pricing?.discountPercentage ?? undefined,
    maxSeats: pricing?.maxSeats ?? 1,
    hasTrial: pricing?.hasTrial ?? false,
    trialDays: pricing?.trialDays ?? 0,
    isActive: pricing?.isActive ?? true,
    isDefault: pricing?.isDefault ?? false,
    displayOrder: pricing?.displayOrder ?? 0,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  // 자동 할인율 계산
  const calculateDiscount = () => {
    if (formData.originalPrice && formData.price) {
      const discount = ((formData.originalPrice - formData.price) / formData.originalPrice * 100).toFixed(0)
      setFormData({ ...formData, discountPercentage: parseInt(discount) })
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-slate-200 z-10">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {pricing ? '가격 정책 수정' : '새 가격 정책'}
            </h2>
            <p className="text-sm text-slate-500 mt-1">{productName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 기본 정보 */}
          <div className="space-y-4">
            <div>
              <label className="label">정책 이름 * (예: "개인 월간", "기업 연간 10석")</label>
              <input
                type="text"
                className="input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="예: 개인 월간 구독"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">가격 유형 *</label>
                <select
                  className="input"
                  value={formData.pricingType}
                  onChange={(e) => setFormData({ ...formData, pricingType: e.target.value as PricingType })}
                >
                  {PRICING_TYPES.map((type) => (
                    <option key={type} value={type}>{PRICING_TYPE_LABELS[type]}</option>
                  ))}
                </select>
              </div>
              {formData.pricingType === 'subscription' && (
                <div>
                  <label className="label">결제 주기</label>
                  <select
                    className="input"
                    value={formData.billingPeriod}
                    onChange={(e) => {
                      const period = e.target.value as BillingPeriod
                      const months = { monthly: 1, quarterly: 3, yearly: 12, lifetime: 0 }[period]
                      setFormData({ ...formData, billingPeriod: period, billingPeriodMonths: months })
                    }}
                  >
                    {BILLING_PERIODS.map((period) => (
                      <option key={period} value={period}>{BILLING_PERIOD_LABELS[period]}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* 가격 */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              가격 설정
            </h3>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">판매가 (원) *</label>
                <input
                  type="number"
                  className="input"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                  onBlur={calculateDiscount}
                  required
                  min={0}
                />
              </div>
              <div>
                <label className="label">정가 (원)</label>
                <input
                  type="number"
                  className="input"
                  value={formData.originalPrice ?? ''}
                  onChange={(e) => setFormData({ ...formData, originalPrice: parseInt(e.target.value) || undefined })}
                  onBlur={calculateDiscount}
                  min={0}
                />
              </div>
              <div>
                <label className="label">할인율 (%)</label>
                <input
                  type="number"
                  className="input"
                  value={formData.discountPercentage ?? ''}
                  onChange={(e) => setFormData({ ...formData, discountPercentage: parseInt(e.target.value) || undefined })}
                  min={0}
                  max={100}
                />
              </div>
            </div>
          </div>

          {/* 좌석 및 체험 */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              좌석 및 체험
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">최대 좌석 수</label>
                <input
                  type="number"
                  className="input"
                  value={formData.maxSeats}
                  onChange={(e) => setFormData({ ...formData, maxSeats: parseInt(e.target.value) || 1 })}
                  min={1}
                />
                <p className="text-xs text-slate-500 mt-1">한 번의 구매로 몇 명이 사용할 수 있는지</p>
              </div>
              <div>
                <label className="label">무료 체험 기간 (일)</label>
                <input
                  type="number"
                  className="input"
                  value={formData.trialDays}
                  onChange={(e) => setFormData({ ...formData, trialDays: parseInt(e.target.value) || 0, hasTrial: parseInt(e.target.value) > 0 })}
                  min={0}
                  disabled={!formData.hasTrial && formData.trialDays === 0}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 rounded-sm border-slate-300 text-amber-600 focus:ring-amber-500"
                checked={formData.hasTrial}
                onChange={(e) => setFormData({ ...formData, hasTrial: e.target.checked, trialDays: e.target.checked ? (formData.trialDays || 7) : 0 })}
              />
              <span className="text-sm font-medium text-slate-700">무료 체험 제공</span>
            </label>
          </div>

          {/* 상태 */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">표시 순서</label>
                <input
                  type="number"
                  className="input"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded-sm border-slate-300 text-amber-600 focus:ring-amber-500"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span className="text-sm font-medium text-slate-700">활성화</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 rounded-sm border-slate-300 text-amber-600 focus:ring-amber-500"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
                <span className="text-sm font-medium text-slate-700">기본 가격 정책</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-secondary btn-md" disabled={isLoading}>
              취소
            </button>
            <button
              type="submit"
              className="btn-primary btn-md"
              disabled={isLoading || !formData.name || formData.price < 0}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  저장 중...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {pricing ? '저장' : '생성'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ============================================================================
// Product Detail Modal
// ============================================================================
interface ProductDetailModalProps {
  product: AdminProduct
  onClose: () => void
  onEdit: () => void
  onAddPricing: () => void
}

function ProductDetailModal({ product, onClose, onEdit, onAddPricing }: ProductDetailModalProps) {
  const { data: pricings } = useQuery({
    queryKey: ['admin', 'products', product.id, 'pricings'],
    queryFn: () => adminApi.getProductPricings(product.id),
  })

  const { data: contents } = useQuery({
    queryKey: ['admin', 'products', product.id, 'contents'],
    queryFn: () => adminApi.getProductContents(product.id),
  })

  const TargetIcon = TARGET_AUDIENCE_ICONS[product.targetAudience ?? 'all'] || Users

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-slate-200 z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{product.name}</h2>
              <p className="text-sm text-slate-500">ID: {product.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onEdit} className="btn-secondary btn-sm">
              <Edit2 className="w-4 h-4 mr-1" />
              수정
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* 상태 태그 */}
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${PRODUCT_STATUS_COLORS[product.status]}`}>
              {PRODUCT_STATUS_LABELS[product.status]}
            </span>
            {product.targetAudience && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-700 flex items-center gap-1">
                <TargetIcon className="w-4 h-4" />
                {TARGET_AUDIENCE_OPTIONS.find(o => o.value === product.targetAudience)?.label ?? product.targetAudience}
              </span>
            )}
            {product.isFeatured && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-700 flex items-center gap-1">
                <Star className="w-4 h-4" />
                추천
              </span>
            )}
            {!product.isActive && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
                비활성
              </span>
            )}
          </div>

          {/* 기본 정보 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-500">슬러그</label>
              <p className="text-slate-900 font-mono">{product.slug || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">카테고리</label>
              <p className="text-slate-900">{product.category || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">태그</label>
              <p className="text-slate-900">{product.tags || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-500">표시 순서</label>
              <p className="text-slate-900">{product.displayOrder}</p>
            </div>
          </div>

          {/* 설명 */}
          {product.shortDescription && (
            <div>
              <label className="text-sm font-medium text-slate-500">짧은 설명</label>
              <p className="text-slate-900 mt-1">{product.shortDescription}</p>
            </div>
          )}

          {product.description && (
            <div>
              <label className="text-sm font-medium text-slate-500">상세 설명</label>
              <p className="text-slate-900 mt-1 whitespace-pre-wrap">{product.description}</p>
            </div>
          )}

          {/* 주요 특징 */}
          {product.features && product.features.length > 0 && (
            <div>
              <label className="text-sm font-medium text-slate-500">주요 특징</label>
              <ul className="mt-2 space-y-1">
                {product.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-slate-900">
                    <Check className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 가격 정책 */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-slate-500">가격 정책</label>
              <button onClick={onAddPricing} className="btn-secondary btn-sm">
                <Plus className="w-4 h-4 mr-1" />
                추가
              </button>
            </div>
            {pricings && pricings.length > 0 ? (
              <div className="space-y-2">
                {pricings.map((pricing) => (
                  <div key={pricing.id} className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{pricing.name}</span>
                          {pricing.isDefault && (
                            <span className="px-2 py-0.5 text-xs rounded-sm bg-amber-100 text-amber-700">기본</span>
                          )}
                          {!pricing.isActive && (
                            <span className="px-2 py-0.5 text-xs rounded-sm bg-slate-200 text-slate-600">비활성</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-600">
                          <span>{PRICING_TYPE_LABELS[pricing.pricingType]}</span>
                          {pricing.pricingType === 'subscription' && (
                            <span>• {BILLING_PERIOD_LABELS[pricing.billingPeriod]}</span>
                          )}
                          <span>• {pricing.maxSeats}석</span>
                          {pricing.hasTrial && (
                            <span className="text-green-600">• {pricing.trialDays}일 체험</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">
                          ₩{pricing.price.toLocaleString()}
                        </p>
                        {pricing.originalPrice && pricing.originalPrice > pricing.price && (
                          <p className="text-sm text-slate-500 line-through">
                            ₩{pricing.originalPrice.toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">등록된 가격 정책이 없습니다</p>
            )}
          </div>

          {/* 포함된 콘텐츠 */}
          <div>
            <label className="text-sm font-medium text-slate-500">포함된 콘텐츠</label>
            {contents && contents.length > 0 ? (
              <div className="mt-2 space-y-1">
                {contents.map((content) => (
                  <div key={content.contentId} className="flex items-center gap-2 p-2 rounded-sm bg-slate-50">
                    <span className="text-sm text-slate-600">#{content.sequenceOrder}</span>
                    <span className="text-sm font-medium text-slate-900">
                      {content.customTitle || content.contentTitle || `콘텐츠 ${content.contentId}`}
                    </span>
                    {!content.isRequired && (
                      <span className="text-xs text-slate-500">(선택)</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm mt-2">연결된 콘텐츠가 없습니다</p>
            )}
          </div>

          {/* 타임스탬프 */}
          <div className="pt-4 border-t border-slate-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <label className="text-slate-500">생성일</label>
                <p className="text-slate-900">{new Date(product.createdAt).toLocaleString('ko-KR')}</p>
              </div>
              <div>
                <label className="text-slate-500">수정일</label>
                <p className="text-slate-900">{new Date(product.updatedAt).toLocaleString('ko-KR')}</p>
              </div>
              {product.publishedAt && (
                <div>
                  <label className="text-slate-500">발행일</label>
                  <p className="text-slate-900">{new Date(product.publishedAt).toLocaleString('ko-KR')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-200">
          <button onClick={onClose} className="btn-secondary btn-md w-full">
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================
export function ProductManagement() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [filterStatus, setFilterStatus] = useState<ProductStatus | ''>('')
  const [filterAudience, setFilterAudience] = useState('')
  const [filterActive, setFilterActive] = useState<boolean | ''>('')

  const [selectedProduct, setSelectedProduct] = useState<AdminProduct | null>(null)
  const [editingProduct, setEditingProduct] = useState<AdminProduct | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [addPricingForProduct, setAddPricingForProduct] = useState<AdminProduct | null>(null)

  // Queries
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'products', { page, keyword, filterStatus, filterAudience, filterActive }],
    queryFn: () => adminApi.getProducts({
      page,
      size: 15,
      keyword: keyword || undefined,
      status: filterStatus || undefined,
      targetAudience: filterAudience || undefined,
      isActive: filterActive === '' ? undefined : filterActive,
    }),
    staleTime: 30 * 1000,
  })

  // Mutations
  const createProductMutation = useMutation({
    mutationFn: (data: CreateProductRequest) => adminApi.createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
      setIsCreating(false)
    },
  })

  const updateProductMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateProductRequest }) => adminApi.updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
      setEditingProduct(null)
    },
  })

  const deleteProductMutation = useMutation({
    mutationFn: (id: number) => adminApi.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
    },
  })

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => adminApi.toggleProductStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
    },
  })

  const createPricingMutation = useMutation({
    mutationFn: (data: CreateProductPricingRequest) => adminApi.createProductPricing(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] })
      setAddPricingForProduct(null)
    },
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(0)
  }

  const handleDelete = (product: AdminProduct) => {
    if (confirm(`"${product.name}" 상품을 삭제하시겠습니까?`)) {
      deleteProductMutation.mutate(product.id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">상품 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-7 h-7 text-amber-600" />
            상품 관리
          </h1>
          <p className="text-slate-600 mt-1">총 {data?.totalElements ?? 0}개의 상품</p>
        </div>
        <button onClick={() => setIsCreating(true)} className="btn-primary btn-md">
          <Plus className="w-5 h-5 mr-2" />
          새 상품
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="상품명, 설명 검색..."
                className="input pl-10"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
          </div>
          <select
            className="input w-32"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value as ProductStatus | ''); setPage(0) }}
          >
            <option value="">모든 상태</option>
            {PRODUCT_STATUSES.map((status) => (
              <option key={status} value={status}>{PRODUCT_STATUS_LABELS[status]}</option>
            ))}
          </select>
          <select
            className="input w-32"
            value={filterAudience}
            onChange={(e) => { setFilterAudience(e.target.value); setPage(0) }}
          >
            <option value="">모든 대상</option>
            {TARGET_AUDIENCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            className="input w-28"
            value={filterActive === '' ? '' : filterActive.toString()}
            onChange={(e) => {
              setFilterActive(e.target.value === '' ? '' : e.target.value === 'true')
              setPage(0)
            }}
          >
            <option value="">활성 여부</option>
            <option value="true">활성</option>
            <option value="false">비활성</option>
          </select>
          <button type="submit" className="btn-primary btn-md">
            검색
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">ID</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">상품명</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">대상</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">상태</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-slate-700">활성</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-slate-700">추천</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.map((product) => {
                const TargetIcon = TARGET_AUDIENCE_ICONS[product.targetAudience ?? 'all'] || Users
                return (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-600">{product.id}</td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-slate-900 max-w-xs truncate">{product.name}</p>
                      {product.shortDescription && (
                        <p className="text-xs text-slate-500 truncate max-w-xs">{product.shortDescription}</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <TargetIcon className="w-4 h-4" />
                        <span>
                          {TARGET_AUDIENCE_OPTIONS.find(o => o.value === product.targetAudience)?.label ?? '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${PRODUCT_STATUS_COLORS[product.status]}`}>
                        {PRODUCT_STATUS_LABELS[product.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleStatusMutation.mutate(product.id)}
                        className="p-1 rounded-sm hover:bg-slate-100"
                        title={product.isActive ? '비활성화' : '활성화'}
                      >
                        {product.isActive ? (
                          <ToggleRight className="w-6 h-6 text-green-600" />
                        ) : (
                          <ToggleLeft className="w-6 h-6 text-slate-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {product.isFeatured && (
                        <Star className="w-5 h-5 text-yellow-500 mx-auto fill-yellow-500" />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setSelectedProduct(product)}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                          title="상세 보기"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setEditingProduct(product)}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                          title="수정"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(product)}
                          className="p-2 hover:bg-slate-100 rounded-lg text-red-500"
                          title="삭제"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {data?.content.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">등록된 상품이 없습니다</p>
            <button
              onClick={() => setIsCreating(true)}
              className="btn-primary btn-md mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              첫 상품 만들기
            </button>
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalElements)} / {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={data.first}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={data.last}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {(isCreating || editingProduct) && (
        <ProductFormModal
          product={editingProduct}
          onClose={() => {
            setIsCreating(false)
            setEditingProduct(null)
          }}
          onSave={(data) => {
            if (editingProduct) {
              updateProductMutation.mutate({ id: editingProduct.id, data })
            } else {
              createProductMutation.mutate(data as CreateProductRequest)
            }
          }}
          isLoading={createProductMutation.isPending || updateProductMutation.isPending}
        />
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onEdit={() => {
            setEditingProduct(selectedProduct)
            setSelectedProduct(null)
          }}
          onAddPricing={() => {
            setAddPricingForProduct(selectedProduct)
            setSelectedProduct(null)
          }}
        />
      )}

      {addPricingForProduct && (
        <PricingFormModal
          pricing={null}
          productId={addPricingForProduct.id}
          productName={addPricingForProduct.name}
          onClose={() => setAddPricingForProduct(null)}
          onSave={(data) => createPricingMutation.mutate(data)}
          isLoading={createPricingMutation.isPending}
        />
      )}
    </div>
  )
}

